/* eslint-disable @typescript-eslint/no-explicit-any */
import ElementAnimation from 'containers/OldNewWordsFrame/ElementAnimation';
import PairGroup from 'containers/OldNewWordsFrame/PairGroup';
import * as d3 from 'd3';
import {debuggableClass, memo} from 'utils/decorators';

export type ViewBoxComps = [number, number, number, number];

@debuggableClass
export default class SvgObject {
  static boundingClientRectCoeff = 0;
  constructor(public svgText: string, public parentDiv: HTMLDivElement, public isMobile: boolean) {}
  @memo get svgRoot(): SVGSVGElement {
    return this.parentDiv.getElementsByTagName('svg')[0];
  }
  @memo get svgRootSel() {
    return d3.select(this.svgRoot);
  }
  @memo get pairGroupsElems() {
    return this.svgRootSel
      .selectAll('svg > g > g > g')
      .nodes()
      .reverse() as SVGGElement[];
  }
  @memo get legendSel() {
    return this.svgRootSel.select('g#legend');
  }
  @memo get animationDelay() {
    return 0;
  }
  @memo get pairGroups() {
    return this.pairGroupsElems
      .filter(g => g.id !== 'legend')
      .map((g, no) => new PairGroup(this, g, () => Number(new Date()) + no * this.animationDelay));
  }
  get viewBoxComps() {
    return this.svgRootSel
      .attr('viewBox')
      .split(' ')
      .map(v => Number(v)) as ViewBoxComps;
  }
  set viewBoxComps(newComps: ViewBoxComps) {
    this.svgRootSel.attr('viewBox', newComps.map(v => v.toString()).join(' '));
  }
  @memo get unitRect() {
    return this.svgRootSel
      .append('rect')
      .attr('id', 'unit-rect')
      .attr('width', 100)
      .attr('height', 100)
      .attr('x', 0)
      .attr('y', 0)
      .attr('fill', 'red')
      .node() as SVGRectElement;
  }
  unmountUnitRect() {
    this.svgRootSel.selectAll('#unit-rect').remove();
    return this;
  }
  @memo get unitRectBboxW() {
    return this.unitRect.getBoundingClientRect().width;
  }
  setBoundingClientRectCoeff() {
    SvgObject.boundingClientRectCoeff = 100 / this.unitRectBboxW;
    this.unmountUnitRect();
    return this;
  }
  padViewBoxH(leftPad: number, rightPad: number) {
    const [x0, y0, w, h] = this.viewBoxComps;
    this.viewBoxComps = [x0 + leftPad, y0, w - leftPad - rightPad, h];
    return this;
  }
  removeSvgSize() {
    this.svgRootSel.attr('width', null).attr('height', null);
    return this;
  }
  removeTextIds() {
    this.svgRootSel.selectAll('text').attr('id', null);
    return this;
  }
  setOpacity(opacity: number) {
    this.parentDiv.style.opacity = opacity.toString();
    return this;
  }
  mount() {
    this.parentDiv.innerHTML = this.svgText;
    return this;
  }
  initialize() {
    this.mount()
      .removeSvgSize()
      .setBoundingClientRectCoeff()
      .removeTextIds()
      .initAnimationsState();
    if (this.isMobile) {
      this.padViewBoxH(0, 0);
    }
    return this;
  }
  @memo get legendAnimation() {
    return new ElementAnimation(this.legendSel.node() as SVGElement, 800, 800, 'FADE_IN');
  }
  initAnimationsState() {
    this.legendAnimation.setInitialState();
    this.pairGroups.forEach(pg => pg.setInitialState());
    return this;
  }
  startAnimations() {
    this.legendAnimation.start();
    this.pairGroups.forEach(pg => pg.startAnimation());
    return this;
  }
  activate() {
    return this.setOpacity(0)
      .initialize()
      .setOpacity(1)
      .startAnimations();
  }
  log(prop: keyof this) {
    console.log((this as any)[prop]);
    return this;
  }
}
